import React, { useCallback, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { FormikProvider, useFormik } from "formik";
import { Button, Modal, Select } from "../..";
import { assignTcByMHSchema } from "../../../utils/validations";

import styles from "./index.module.css";
import { useSameDayRoundTripConfirmationModal } from "../SameDayRoundTripConfirmationModal/useSameDayRoundTripConfirmationModal";
import { CONFIRM_ROUNDTRIP_RIDE_CONTENT, CONFIRM_ROUNDTRIP_RIDE_TITLE } from "../../../constants/constants";

const emptyValues = { transportCompanyId: "", tcDriverId: "" };

export const useAssignTCByMHModal = ({
  id,
  ride,
  role,
  tcOptions,
  driversOptions,
  rideCurrentValues = emptyValues,
  onConfirm,
  changeTC: changeTCRes,
}) => {
  const [visibleModal, setModalVisible] = useState(false);
  const valuesRef = useRef(emptyValues);
  const setCancelModalVisibleRef = useRef(() => {});

  const confirmRoundTrip = useCallback(() => {
    onConfirm({ ...valuesRef.current });
    return setCancelModalVisibleRef.current(false);
  }, [onConfirm]);

  const { Modal: ModalRoundTripConfirmation, setCancelModalVisible } =
    useSameDayRoundTripConfirmationModal({
      id: ride?.seqId,
      role,
      title: CONFIRM_ROUNDTRIP_RIDE_TITLE,
      description: CONFIRM_ROUNDTRIP_RIDE_CONTENT,
      ride: ride,
      onConfirm: confirmRoundTrip,
    });

  setCancelModalVisibleRef.current = setCancelModalVisible;

  const handleConfirm = useCallback(
    (formValues) => {
      if (ride?.connectedLeg) {
        setCancelModalVisible(true);
      } else {
        onConfirm({ ...formValues });
      }
    },
    [onConfirm, ride?.connectedLeg, setCancelModalVisible]
  );

  const formik = useFormik({
    initialValues: rideCurrentValues,
    validationSchema: assignTcByMHSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: handleConfirm,
  });
  const { resetForm, isValid, values, handleSubmit, setValues } = formik;
  valuesRef.current = values;

  // Fetch drivers when open with a TC selected (do not resetForm here — it loops).
  useEffect(() => {
    if (visibleModal && values?.transportCompanyId) {
      changeTCRes({
        transportCompanyId: values.transportCompanyId,
        rideId: ride?.id,
      });
    }
  }, [visibleModal, changeTCRes, ride?.id, values?.transportCompanyId]);

  const setModalAssignTCVisible = useCallback(
    (visible) => {
      if (visible) {
        setValues({
          transportCompanyId: rideCurrentValues?.transportCompanyId || "",
          tcDriverId: rideCurrentValues?.tcDriverId || "",
        });
      } else {
        resetForm({ values: emptyValues });
      }
      setModalVisible(visible);
    },
    [
      resetForm,
      setValues,
      rideCurrentValues?.transportCompanyId,
      rideCurrentValues?.tcDriverId,
    ]
  );

  const onCancel = useCallback(() => {
    setModalAssignTCVisible(false);
  }, [setModalAssignTCVisible]);

  const handleChangeTC = async ({ name, value, setFieldValue }) => {
    await setFieldValue(name, value);
    await setFieldValue("tcDriverId", "");
    await changeTCRes({ transportCompanyId: value, rideId: ride?.id });
  };

  const handleChangeDriver = useCallback(
    async ({ name, value, setFieldValue }) => {
      await setFieldValue(name, value);
    },
    []
  );

  return {
    Modal: (
      <Modal
        visible={visibleModal}
        onCancel={onCancel}
        width={670}
        bodyStyle={{ padding: '8px 24px 24px' }}
        title={`Assign Driver to Ride HO – ID ${id}`}
      >
        <div className={styles.modalContent}>
          <FormikProvider value={formik}>
            <>
              <form>
                {tcOptions?.length > 0 && (
                  <label className={styles.cancelRideLabel}>
                    <span className={styles.required}>Select TC</span>
                    <Select
                      name="transportCompanyId"
                      options={tcOptions}
                      onChange={handleChangeTC}
                      placeholder="Pick Transport Company"
                    />
                  </label>
                )}
                {(
                  <label className={styles.cancelRideLabel}>
                    <span className={styles.required}>Select Driver</span>
                    <Select
                      name="tcDriverId"
                      placement={"bottomLeft"}
                      options={driversOptions}
                      onChange={handleChangeDriver}
                      disabled={!driversOptions?.length > 0}
                      placeholder={driversOptions?.length > 0 ? "Pick Driver" : "No Drivers Available"}
                    />
                  </label>
                )}
              </form>
              <div
                className={classnames({
                  [styles.modalBtns]: true,
                  [styles.cancelBtns]: true,
                })}
              >
                <div className={styles.mr6}>
                  <Button myClassName={styles.navigationBtn} onClick={onCancel}>
                    Cancel
                  </Button>
                </div>
                <div className={styles.ml6}>
                  <Button
                    myClassName={styles.navigationBtn}
                    onClick={handleSubmit}
                    disabled={!isValid}
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </>
          </FormikProvider>
        </div>
        {ModalRoundTripConfirmation}
      </Modal>
    ),
    setModalAssignTCVisible,
  };
};
